import React from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { LANGUAGES_SIMPLE } from "@/lib/languages";
import ScrollReveal from "@/components/ScrollReveal";
import OptimizedImage from "@/components/OptimizedImage";
import { useSignIn } from "@/hooks/useSignIn";

// Supported languages - matching what's shown in My Content tab article settings
const POPULAR_LANGUAGE_CODES = [
  // Major European Languages
  'en', 'es', 'fr', 'de', 'it', 'pt', 'nl', 'pl', 'sv', 'no', 'da', 'fi', 'cs', 'hu', 'ro', 'bg', 'hr', 'sk', 'sl', 'tr', 'uk', 'el', 'lt', 'lv', 'et', 'mk', 'sq', 'sr', 'bs', 'me', 'ca', 'eu', 'gl', 'cy', 'ga', 'gd', 'mt', 'is', 'fo', 'lb',
  // Asian Languages
  'ja', 'ko', 'zh', 'zh-tw', 'hi', 'bn', 'ur', 'ta', 'te', 'ml', 'kn', 'gu', 'pa', 'th', 'vi', 'id', 'ms', 'tl', 'my', 'km', 'lo',
  // Middle Eastern & African Languages
  'ar', 'fa', 'he', 'am', 'sw', 'ha', 'yo', 'ig', 'zu', 'af', 'xh',
  // Americas Regional Variants
  'pt-br', 'es-mx', 'es-ar', 'fr-ca'
];

// Get only the supported languages (excluding 'ru' and 'be')
const SUPPORTED_LANGUAGES = LANGUAGES_SIMPLE
  .filter(l => POPULAR_LANGUAGE_CODES.includes(l.value))
  .filter(l => l.value !== 'ru' && l.value !== 'be');

interface FeaturesProps {
  onSignIn: () => void;
}

const Features: React.FC<FeaturesProps> = ({ onSignIn }) => {
  const { signingIn } = useSignIn();
  
  const handleClick = async () => {
    if (signingIn) return;
    await onSignIn();
  };
  
  return (
    <section id="features" className="w-full py-12 sm:py-16 lg:py-20 scroll-mt-24">
      <div className="container mx-auto px-2 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center sm:text-left mb-8 sm:mb-12 lg:mb-16">
            <div className="mb-3 sm:mb-4 lg:mb-6">
              <h1 className="text-xs sm:text-sm md:text-base font-bold text-foreground/90 uppercase tracking-wider">
                FEATURES
              </h1>
            </div>
            <div className="flex flex-col lg:flex-row items-center sm:items-start lg:items-end gap-4 lg:gap-24">
              <div className="flex items-end gap-0 w-full lg:w-auto justify-center sm:justify-start">
                <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-foreground leading-tight">
                  Everything you need<br />
                  <span className="inline-flex items-end gap-2 sm:gap-3 md:gap-4">
                    to <span className="text-primary whitespace-nowrap">dominate search</span>
                    <OptimizedImage
                      src="https://ancujowsxivyfnxhnsmr.supabase.co/storage/v1/object/sign/random%20images/arrow-right-black.538c912a.svg?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8xOWQwMmZkMy05MzllLTQ3YWYtODEyYS0zOWQxMTc0NGY2N2IiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJyYW5kb20gaW1hZ2VzL2Fycm93LXJpZ2h0LWJsYWNrLjUzOGM5MTJhLnN2ZyIsImlhdCI6MTc2Mzg1MTIwOSwiZXhwIjoxOTIxNTMxMjA5fQ.bBnvaFY4fcPXFcUI3k4ZcGcVmjjZsJVbgvuLZpQNQXI"
                      alt=""
                      width={24}
                      height={24}
                      className="h-3 sm:h-4 md:h-5 lg:h-6 w-auto hidden sm:block flex-shrink-0 mb-1 sm:mb-1.5"
                      aria-hidden="true"
                    />
                  </span>
                </h2>
              </div>
              <div className="flex flex-col items-center sm:items-start gap-2 lg:gap-1 w-full lg:w-auto lg:-mt-8">
                <p className="text-sm sm:text-base md:text-lg text-muted-foreground max-w-2xl text-center sm:text-left mb-0 lg:transform lg:-translate-y-5">
                  Get SEO-optimized articles, images, and backlinks delivered automatically. One platform that handles everything—so you can focus on growth.
                </p>
              </div>
            </div>
          </div>

          {/* Main Two-Section Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 mb-10 sm:mb-16 items-stretch">
            {/* Left Section - Intelligent Topic Clusters */}
            <ScrollReveal direction="up" delay={0}>
              <div className="backdrop-blur-sm rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-10 shadow-sm border border-border/50 transition-shadow h-full" style={{ background: 'linear-gradient(to right, oklch(var(--primary) / 0.07), oklch(var(--primary) / 0.035), transparent 45%, oklch(var(--background) / 0.95))' }}>
              <div className="flex flex-col h-full">
                <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-foreground mb-4 max-w-lg">
                  Intelligent Topic Clusters & Ideation
                </h3>
                <p className="text-sm sm:text-base text-muted-foreground mb-6 leading-relaxed flex-grow max-w-lg">
                  Our AI analyzes your niche to create structured topic clusters and generate 30 fresh article ideas every month. So you can stop guessing what to write about and start ranking.
                </p>
                <Button
                  onClick={handleClick}
                  disabled={signingIn}
                  className="w-auto sm:w-fit rounded-full text-sm sm:text-base px-5 sm:px-6 py-2.5 sm:py-3 font-semibold bg-gradient-to-r from-primary via-primary to-primary/90 text-primary-foreground hover:from-primary/95 hover:via-primary/90 hover:to-primary/85 transition-all duration-300 flex items-center justify-center gap-2 shadow-sm self-start disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span>Get Started Now</span>
                  <ArrowRight className="h-3.5 w-3.5 sm:h-4 sm:w-4 group-hover:translate-x-1 transition-transform duration-200" />
                </Button>
              </div>
              </div>
            </ScrollReveal>

            {/* Right Section - Feature Image */}
            <ScrollReveal direction="up" delay={50}>
              <div className="bg-background/80 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-10 shadow-sm border border-border/50 transition-shadow h-full">
              <div className="flex flex-col h-full">
                <div className="w-full h-full rounded-xl sm:rounded-2xl overflow-hidden flex-grow">
                  <OptimizedImage
                    src="https://ancujowsxivyfnxhnsmr.supabase.co/storage/v1/object/sign/random%20images/Topic%20clusters%20and%20ideas3.png?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8xOWQwMmZkMy05MzllLTQ3YWYtODEyYS0zOWQxMTc0NGY2N2IiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJyYW5kb20gaW1hZ2VzL1RvcGljIGNsdXN0ZXJzIGFuZCBpZGVhczMucG5nIiwiaWF0IjoxNzY0MDE4MzE4LCJleHAiOjE5MjE2OTgzMTh9.bW_TPgUdWXuMguMmPQc7oZNrC8Ct0nxGwrloA41WgrI"
                    alt="Intelligent Topic Clusters & Ideation"
                    width={800}
                    height={600}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              </div>
            </ScrollReveal>
          </div>

          {/* Bottom Section - Create content that naturally ranks */}
          <ScrollReveal direction="up" delay={100}>
            <div className="backdrop-blur-sm rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-10 shadow-sm border border-border/50 transition-shadow mb-6 sm:mb-8" style={{ background: 'linear-gradient(to right, oklch(var(--primary) / 0.05), oklch(var(--primary) / 0.025), transparent 40%, oklch(var(--background) / 0.95))' }}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 items-center">
              <div className="max-w-lg">
                <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-foreground mb-4">
                  Create content that naturally ranks
                </h3>
                <div className="mb-6">
                  <p className="text-sm sm:text-base text-muted-foreground mb-2 leading-relaxed">
                    Get SEO-ready articles that read naturally, based on powerful keywords. Every piece automatically includes strategic internal or external links.
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">Available in {SUPPORTED_LANGUAGES.length} languages</span>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button className="text-primary hover:text-primary/80 transition-colors">
                            <Info className="h-3.5 w-3.5" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-sm p-4 bg-background border border-border/50 shadow-lg rounded-xl">
                          <div className="space-y-2">
                            <p className="text-sm font-semibold text-foreground mb-2">Supported Languages ({SUPPORTED_LANGUAGES.length})</p>
                            <div className="grid grid-cols-2 gap-1.5 max-h-64 overflow-y-auto">
                              {SUPPORTED_LANGUAGES.map((lang) => (
                                <div key={lang.value} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                  <span>{lang.flag}</span>
                                  <span className="truncate">{lang.label}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </div>
                <Button
                  onClick={() => {
                    const element = document.getElementById('writing-examples');
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }}
                  variant="outline"
                  className="w-auto sm:w-fit rounded-full text-sm sm:text-base px-5 sm:px-6 py-2.5 sm:py-3 font-semibold border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-all duration-300 flex items-center justify-center gap-2 self-start"
                >
                  <span>Read Examples</span>
                  <ArrowRight className="h-3.5 w-3.5 sm:h-4 sm:w-4 group-hover:translate-x-1 transition-transform duration-200" />
                </Button>
              </div>
              <div className="w-full aspect-video rounded-xl sm:rounded-2xl overflow-hidden">
                <OptimizedImage
                  src="https://ancujowsxivyfnxhnsmr.supabase.co/storage/v1/object/sign/random%20images/rank%20higher%20lang%20showcase-Photoroom%202.png?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8xOWQwMmZkMy05MzllLTQ3YWYtODEyYS0zOWQxMTc0NGY2N2IiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJyYW5kb20gaW1hZ2VzL3JhbmsgaGlnaGVyIGxhbmcgc2hvd2Nhc2UtUGhvdG9yb29tIDIucG5nIiwiaWF0IjoxNzY0MDE3OTg3LCJleHAiOjE5MjE2OTc5ODd9.30-KBWOT3Y1O0ksYlEQDmLcEkKnV1MTQPvPNGkiHyVA"
                  alt="Create content that naturally ranks"
                  width={1200}
                  height={675}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            </div>
          </ScrollReveal>

          {/* Feature 3 */}
          <ScrollReveal direction="up" delay={150}>
            <div className="backdrop-blur-sm rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-10 shadow-sm border border-border/50 transition-shadow mb-6 sm:mb-8" style={{ background: 'linear-gradient(to left, oklch(var(--primary) / 0.07), oklch(var(--primary) / 0.035), transparent 45%, oklch(var(--background) / 0.95))' }}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 items-center">
              <div className="w-full aspect-video rounded-xl sm:rounded-2xl overflow-hidden order-2 lg:order-1">
                <OptimizedImage
                  src="https://ancujowsxivyfnxhnsmr.supabase.co/storage/v1/object/sign/random%20images/google%20index%20showcase8.jpg?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8xOWQwMmZkMy05MzllLTQ3YWYtODEyYS0zOWQxMTc0NGY2N2IiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJyYW5kb20gaW1hZ2VzL2dvb2dsZSBpbmRleCBzaG93Y2FzZTguanBnIiwiaWF0IjoxNzY0MDE4NDE4LCJleHAiOjE5MjE2OTg0MTh9.rjoqfh-si-jyxt3Ov1cQwvrVqLDHV6V2lArm1W1x-RQ"
                  alt="Track performance in real-time"
                  width={1200}
                  height={675}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="order-1 lg:order-2 max-w-lg lg:ml-auto">
                <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-foreground mb-4">
                  Track performance in real-time
                </h3>
                <p className="text-sm sm:text-base text-muted-foreground mb-6 leading-relaxed">
                  Connect your Google Search Console and see your actual performance data in the dashboard. Track clicks, impressions, rankings, and indexing status—all in one place.
                </p>
                <Button
                  onClick={onSignIn}
                  className="w-auto sm:w-fit rounded-full text-sm sm:text-base px-5 sm:px-6 py-2.5 sm:py-3 font-semibold bg-gradient-to-r from-primary via-primary to-primary/90 text-primary-foreground hover:from-primary/95 hover:via-primary/90 hover:to-primary/85 transition-all duration-300 flex items-center justify-center gap-2 shadow-sm self-end sm:self-start"
                >
                  <span>Get Started Now</span>
                  <ArrowRight className="h-3.5 w-3.5 sm:h-4 sm:w-4 group-hover:translate-x-1 transition-transform duration-200" />
                </Button>
              </div>
            </div>
            </div>
          </ScrollReveal>

          {/* Feature 4 */}
          <ScrollReveal direction="up" delay={200}>
            <div className="backdrop-blur-sm rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-10 shadow-sm border border-border/50 transition-shadow mb-6 sm:mb-8" style={{ background: 'linear-gradient(to right, oklch(var(--primary) / 0.05), oklch(var(--primary) / 0.025), transparent 40%, oklch(var(--background) / 0.95))' }}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 items-center">
              <div className="max-w-lg">
                <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-foreground mb-4">
                  Generate high-quality images
                </h3>
                <p className="text-sm sm:text-base text-muted-foreground mb-6 leading-relaxed">
                  Choose from multiple image styles (realistic, cartoon, and more) and set the quantity. We use the newest, most advanced image models to generate article-relevant visuals that auto-insert throughout your content.
                </p>
                <Button
                  onClick={handleClick}
                  disabled={signingIn}
                  className="w-auto sm:w-fit rounded-full text-sm sm:text-base px-5 sm:px-6 py-2.5 sm:py-3 font-semibold bg-gradient-to-r from-primary via-primary to-primary/90 text-primary-foreground hover:from-primary/95 hover:via-primary/90 hover:to-primary/85 transition-all duration-300 flex items-center justify-center gap-2 shadow-sm self-start disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span>Get Started Now</span>
                  <ArrowRight className="h-3.5 w-3.5 sm:h-4 sm:w-4 group-hover:translate-x-1 transition-transform duration-200" />
                </Button>
              </div>
              <div className="w-full aspect-video rounded-xl sm:rounded-2xl overflow-hidden">
                <OptimizedImage
                  src="https://ancujowsxivyfnxhnsmr.supabase.co/storage/v1/object/sign/random%20images/AI%20IMAGE%20GENERATOR2.png?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8xOWQwMmZkMy05MzllLTQ3YWYtODEyYS0zOWQxMTc0NGY2N2IiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJyYW5kb20gaW1hZ2VzL0FJIElNQUdFIEdFTkVSQVRPUjIucG5nIiwiaWF0IjoxNzY0MDI3NTY5LCJleHAiOjE5MjE3MDc1Njl9.j0uYtRQfp8BJGasbKmgd-fnpsOFnhhaL2SNmsr9Li0I"
                  alt="Generate high-quality images"
                  width={1200}
                  height={675}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            </div>
          </ScrollReveal>

          {/* Feature 5 - Seamless CMS Integration */}
          <ScrollReveal direction="up" delay={250}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 mb-12 sm:mb-16">
              {/* Left Section - Publishing Image */}
              <div className="bg-background/80 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-10 shadow-sm border border-border/50 transition-shadow order-2 lg:order-1">
              <div className="flex flex-col h-full">
                <div className="w-full aspect-video rounded-xl sm:rounded-2xl overflow-hidden">
                  <OptimizedImage
                    src="https://ancujowsxivyfnxhnsmr.supabase.co/storage/v1/object/sign/random%20images/publishing%20illustartionj.png?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8xOWQwMmZkMy05MzllLTQ3YWYtODEyYS0zOWQxMTc0NGY2N2IiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJyYW5kb20gaW1hZ2VzL3B1Ymxpc2hpbmcgaWxsdXN0YXJ0aW9uai5wbmciLCJpYXQiOjE3NjQwMjAxNTcsImV4cCI6MTkyMTcwMDE1N30.4ySTY9h201RCRzQsVHyYFn278HBZW7IdjL47nGJor8Q"
                    alt="Publish anywhere, instantly"
                    width={1200}
                    height={675}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              </div>

              {/* Right Section - Publish Anywhere */}
              <div className="backdrop-blur-sm rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-10 shadow-sm border border-border/50 transition-shadow order-1 lg:order-2" style={{ background: 'linear-gradient(to left, oklch(var(--primary) / 0.07), oklch(var(--primary) / 0.035), transparent 45%, oklch(var(--background) / 0.95))' }}>
              <div className="flex flex-col h-full">
                <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-foreground mb-4 max-w-lg">
                  Publish Instantly with Integrations
                </h3>
                <p className="text-sm sm:text-base text-muted-foreground mb-6 leading-relaxed flex-grow max-w-lg">
                  Send your content straight to where it matters most. One click and you're done—no copying, no formatting, no hassle. Works with Shopify, WordPress, Webflow, and other popular CMS platforms. Framer integration coming soon! Your content goes live exactly where your audience is. Prefer to work offline? Download your articles as files or copy the complete HTML with all images included—whatever works best for your workflow.
                </p>
                <Button
                  onClick={handleClick}
                  disabled={signingIn}
                  className="w-auto sm:w-fit rounded-full text-sm sm:text-base px-5 sm:px-6 py-2.5 sm:py-3 font-semibold bg-gradient-to-r from-primary via-primary to-primary/90 text-primary-foreground hover:from-primary/95 hover:via-primary/90 hover:to-primary/85 transition-all duration-300 flex items-center justify-center gap-2 shadow-sm self-start disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span>Get Started Now</span>
                  <ArrowRight className="h-3.5 w-3.5 sm:h-4 sm:w-4 group-hover:translate-x-1 transition-transform duration-200" />
                </Button>
              </div>
            </div>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
};

export default Features;
